import { CheckIcon, Cross2Icon } from '@radix-ui/react-icons'
import { cva } from 'class-variance-authority'
import { CommandList } from 'cmdk'
import { cn } from 'lib/utils'
import { ChevronDown, Loader2 } from 'lucide-react'
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem } from 'primitives/command'
import { Popover, PopoverContent, PopoverTrigger } from 'primitives/popover'
import { Tooltip } from 'primitives/tooltip'
import React from 'react'
import { Virtuoso } from 'react-virtuoso'
import { Label } from './label'
import { Typography } from './typography'

export interface SelectOption {
  value: string
  label: string | React.ReactNode
  icon?: React.ReactNode
  /**
   * The value that will be used to filter the options
   * @default label
   * */
  searchValue?: string
  /** Native hover text; defaults to `searchValue`, then `label`. `''` turns it off. */
  title?: string
  disabled?: boolean
  tooltipContent?: string
  [key: string]: unknown
}

export interface SelectBoxProps {
  name?: string
  label?: string | React.ReactNode
  options: SelectOption[]
  value?: number | string[] | string
  onChange?: (values: string[] | string) => void
  placeholder?: string
  inputPlaceholder?: string
  emptyPlaceholder?: string
  className?: string
  multiple?: boolean
  clearable?: boolean
  searchable?: boolean
  disabled?: boolean
  variant?: 'default' | 'ghost'
  popoverContentClassName?: string
  size?: 'sm' | 'md' | 'lg'
  helperText?: string
  error?: React.ReactNode
  loading?: boolean
  inline?: boolean
  /**
   * Opt out of the >6-options Virtuoso list for SHORT STATIC option sets that
   * should render in full (e.g. the SIP platform picker). Virtualization is for
   * long/remote lists; a fixed 200px scroller on a 7-item list hides options —
   * and under happy-dom (viewport height 0) hides them from tests entirely.
   */
  virtualized?: boolean
  // Remote select props
  onSearchChange?: (searchTerm: string) => void
  onLoadMore?: () => void
  onOpenChange?: (open: boolean) => void
  selectedValueRenderer?: React.ComponentType<{
    value: number | string[] | string
    options: SelectOption[]
  }>
}

interface OptionItemProps {
  option: SelectOption
  value: number | string[] | string | undefined
  handleSelect: (value: string) => void
  multiple: boolean
}

const OptionItem: React.FC<OptionItemProps> = ({ option, value, handleSelect, multiple }) => {
  const isSelected = Array.isArray(value) && value.includes(option.value)

  const content = (
    <CommandItem
      key={option.value}
      onSelect={() => !option.disabled && handleSelect(option.value)}
      disabled={option.disabled}
      data-testid={option.value}
      className={cn(
        'min-h-10 rounded-md px-3 py-2 font-inter text-s-400 text-foreground aria-selected:bg-hv1',
        (Array.isArray(value) ? value.includes(option.value) : value === option.value) && 'bg-hv1',
        option.disabled && 'cursor-not-allowed opacity-50',
      )}
    >
      {multiple && (
        <div
          className={cn(
            'mr-2 flex h-4 w-4 items-center justify-center rounded-sm border border-st2',
            isSelected
              ? 'border-brand bg-brand text-brand-foreground'
              : 'opacity-70 [&_svg]:invisible',
          )}
        >
          <CheckIcon />
        </div>
      )}
      {/* added whitespace-normal break-words to allow long words to break */}
      <span
        className="truncate flex items-center gap-2 whitespace-normal break-words flex-1"
        title={option.title ?? (option.searchValue || (option.label as string))}
        data-test-value={option.value}
      >
        {option.icon} {option.label}
      </span>
      {!multiple && option.value === value && (
        <CheckIcon
          className={cn(
            'ml-auto size-4 shrink-0 text-brand-text',
            option.value === value ? 'opacity-100' : 'opacity-0',
          )}
        />
      )}
    </CommandItem>
  )

  if (option.disabled && option.tooltipContent) {
    return (
      <Tooltip content={option.tooltipContent} side="right">
        <div>{content}</div>
      </Tooltip>
    )
  }

  return content
}

interface MultiSelectValueProps {
  option: SelectOption
  handleSelect: (value: string) => void
}
const MultiSelectValue: React.FC<MultiSelectValueProps> = ({ option, handleSelect }) => (
  <span className="inline-flex max-w-[150px] items-center gap-1 rounded-md border border-brand/20 bg-hv1 py-0.5 pl-2 pr-1 font-inter text-s-400 normal-case tracking-normal text-foreground transition-colors focus:outline-none focus:ring-2 focus:ring-brand focus:ring-offset-2">
    {/* Truncate long labels */}
    <span className="truncate max-w-full" title={option.searchValue || (option.label as string)}>
      {(option.badgeLabel as React.ReactNode) ?? option.label}
    </span>
    <button
      aria-label={`Remove ${option.label}`}
      type="button"
      onClick={(e) => {
        e.preventDefault()
        handleSelect(option.value)
      }}
      className="flex items-center rounded-sm px-[1px] text-secondary hover:text-foreground focus-visible"
    >
      <Cross2Icon />
    </button>
  </span>
)

const selectStyles = cva('font-inter text-p-400 normal-case tracking-normal', {
  variants: {
    variant: {
      ghost: 'w-28',
      default: 'w-full',
    },
    size: {
      sm: 'min-h-[26px]',
      md: 'min-h-[36px]',
      lg: 'min-h-[42px]',
    },
  },
  defaultVariants: {
    variant: 'default',
    size: 'md',
  },
})

export const Select = React.forwardRef<HTMLInputElement, SelectBoxProps>(
  (
    {
      inputPlaceholder,
      emptyPlaceholder,
      placeholder,
      className,
      options,
      value,
      label,
      onChange,
      multiple,
      disabled,
      clearable = false,
      searchable = true,
      variant = 'default',
      size = 'md',
      name,
      popoverContentClassName = 'w-[var(--radix-popover-trigger-width)]',
      helperText = '',
      error,
      loading = false,
      inline = false,
      virtualized = true,
      // Remote select props
      onSearchChange, // this means search is remote
      onLoadMore,
      onOpenChange,
      selectedValueRenderer: SelectedValueRenderer,
    },
    ref,
  ) => {
    const [searchTerm, setSearchTerm] = React.useState<string>('')
    const [isOpenInternal, setIsOpenInternal] = React.useState(false)
    const triggerButtonRef = React.useRef<HTMLButtonElement>(null)

    // Use external handlers if provided
    const handleSetIsOpen = (open: boolean) => {
      setIsOpenInternal(open)
      onOpenChange?.(open)
    }

    const handleSearchChange = (term: string) => {
      setSearchTerm(term)
      onSearchChange?.(term)
    }

    // Filter options based on search term (only if no remote search)
    const filteredOptions = onSearchChange
      ? options
      : options?.filter((option) => {
          if (typeof option.label === 'string') {
            return option.label.toLowerCase().includes(searchTerm.toLowerCase())
          }
          return option.searchValue?.toLowerCase().includes(searchTerm.toLowerCase())
        })

    // Handle selection
    const handleSelect = (selectedValue: string) => {
      if (!selectedValue) return // Guard against empty values

      if (multiple && Array.isArray(value)) {
        const newValue = value.includes(selectedValue)
          ? value.filter((v) => v !== selectedValue)
          : [...value, selectedValue]
        onChange?.(newValue)
      } else {
        onChange?.(multiple ? [selectedValue] : selectedValue)
        if (!multiple) {
          handleSetIsOpen(false)
        }
      }
      if (!onSearchChange) {
        setSearchTerm('')
      }
    }

    // Handle clearing selection
    const handleClear = () => {
      onChange?.(multiple ? [] : '')
    }

    const displayMultipleValues = multiple && Array.isArray(value) && value.length > 0
    // Preserve selection order by mapping value array to options. A value with
    // no matching option (e.g. a legacy/stored value outside the current
    // option list) still gets a fallback entry so it stays visible instead of
    // silently vanishing from the selected state — mirrors the single-select
    // fallback rendering below.
    const selectedOptions = Array.isArray(value)
      ? value.map(
          (val): SelectOption =>
            options?.find((option) => option.value === val) ?? {
              value: val,
              label: val,
            },
        )
      : []

    // Virtuoso virtualized list if more than 6 options (unless opted out)
    const comp =
      virtualized && filteredOptions?.length > 6 ? (
        <Virtuoso
          itemContent={(_, option) => (
            <OptionItem
              key={option.value}
              option={option}
              value={value}
              handleSelect={handleSelect}
              multiple={multiple || false}
            />
          )}
          data={filteredOptions}
          style={{ height: 200 }}
          endReached={onLoadMore}
        />
      ) : (
        filteredOptions?.map((option) => (
          <OptionItem
            key={option.value}
            option={option}
            value={value}
            handleSelect={handleSelect}
            multiple={multiple || false}
          />
        ))
      )

    // Render selected values or placeholder based on conditions
    let content: React.ReactNode
    if (displayMultipleValues) {
      // Multiple selection
      content = (
        <>
          {selectedOptions.map((option) => (
            <MultiSelectValue key={option.value} option={option} handleSelect={handleSelect} />
          ))}
          {/* {selectedOptions.length > 1 && (
            <button
              type="button"
              className="inline-flex items-center gap-1 rounded-md bg-bg3 py-0.5 pl-2 pr-1 font-inter text-s-400 normal-case tracking-normal text-foreground truncate max-w-[150px] transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
              onClick={() => setIsOpen(true)} // Open dropdown when clicked
            >
              +{selectedOptions.length - 1}
            </button>
          )} */}
        </>
      )
    } else if (value && !multiple) {
      // Use custom renderer if provided
      if (SelectedValueRenderer) {
        content = <SelectedValueRenderer value={value} options={options} />
      } else {
        const selectedOption = options?.find((opt) => opt.value === value)
        // Only show if we have a valid selected option
        content = selectedOption ? (
          <span className="truncate flex items-center gap-2">
            {selectedOption.icon} {selectedOption.label}
          </span>
        ) : (
          <span className="truncate flex items-center gap-2 text-status-danger">{value}</span>
        )
      }
    } else {
      content = <span className="mr-auto text-muted-foreground">{placeholder}</span>
    }

    const dataState = () => {
      if (loading) return 'closed'
      if (isOpenInternal) return 'open'
      return 'closed'
    }

    return (
      <Popover open={isOpenInternal} onOpenChange={handleSetIsOpen} modal data-state={dataState()}>
        <PopoverTrigger
          asChild
          className={cn(disabled && 'pointer-events-none', loading && 'pointer-events-none')}
        >
          <div className={cn(selectStyles({ variant, size }), className)}>
            {label && typeof label === 'string' ? (
              <Label className="mb-1.5 block" htmlFor={name}>
                {label}
              </Label>
            ) : (
              label
            )}
            <button
              ref={triggerButtonRef}
              type="button"
              id={name}
              role="combobox"
              aria-controls={name}
              aria-expanded={isOpenInternal}
              className={cn(
                'flex w-full cursor-pointer items-center px-3 py-1 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand/20',
                selectStyles({ size }),
                {
                  'justify-end border-none focus:ring-0 w-20': variant === 'ghost',
                  'justify-between rounded-md border border-st2 bg-background hover:border-brand/60':
                    variant === 'default',
                },
                disabled || loading ? 'bg-muted text-muted-foreground pointer-events-none' : '',
                (disabled || loading) && variant === 'ghost' ? 'bg-transparent text-tertiary' : '',
                isOpenInternal && variant !== 'ghost' && 'border-brand ring-2 ring-brand/20',
                error && 'border-destructive',
                className,
              )}
            >
              <div
                className={cn(
                  'flex items-center gap-1 overflow-hidden w-full',
                  multiple ? 'flex-grow flex-wrap' : 'truncate',
                )}
              >
                {content}
              </div>

              {/* Update the icons section */}
              {!inline && (
                <div className="flex items-center pl-1 text-secondary transition-colors hover:text-foreground">
                  {loading ? (
                    <Loader2 className="size-4 animate-spin" />
                  ) : clearable && (Array.isArray(value) ? value.length > 0 : value) ? (
                    <button
                      aria-label="Clear"
                      type="button"
                      onClick={(e) => {
                        e.preventDefault()
                        handleClear()
                      }}
                    >
                      <Cross2Icon className="size-4" />
                    </button>
                  ) : (
                    <ChevronDown className="size-4" />
                  )}
                </div>
              )}
            </button>

            {helperText && (
              <Typography variant="XS/400" className="text-tertiary mt-2">
                {helperText}
              </Typography>
            )}
            {error && (
              <Typography variant="XS/400" className="text-status-danger mt-2">
                {error}
              </Typography>
            )}
          </div>
        </PopoverTrigger>

        <PopoverContent
          onCloseAutoFocus={(e) => {
            e.preventDefault()
            triggerButtonRef.current?.focus()
          }}
          className={cn(
            'overflow-hidden rounded-md border border-border bg-popover p-0 text-popover-foreground shadow-bs1',
            popoverContentClassName,
          )}
          align="start"
        >
          <Command shouldFilter={false}>
            {searchable && (
              <div className="relative">
                <CommandInput
                  value={searchTerm}
                  onValueChange={handleSearchChange}
                  ref={ref}
                  placeholder={inputPlaceholder ?? 'Search...'}
                  className="h-10 font-inter text-s-400"
                />
                {searchTerm && (
                  <button
                    aria-label="Clear search"
                    type="button"
                    className="absolute inset-y-0 right-0 flex cursor-pointer items-center pr-3 text-muted-foreground hover:text-foreground"
                    onClick={() => handleSearchChange('')}
                  >
                    <Cross2Icon className="size-4" />
                  </button>
                )}
              </div>
            )}
            <CommandEmpty>{emptyPlaceholder ?? 'No results found.'}</CommandEmpty>
            <CommandList>
              <CommandGroup>{comp}</CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    )
  },
)

Select.displayName = 'Select'
